import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCirclePlus,
  faChevronDown,
  faChevronUp,
  faFloppyDisk,
  faXmark,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import EditButtonComponent from "../../../components/EditButtonComponent";
import DeleteButtonComponent from "../../../components/DeleteButtonComponent";
import { Col, Row } from "react-bootstrap";
import ViewButtonComponentV2 from "../../../components/viewButtonComponentV2";
import ButtonComponent from "../../../components/ButtonComponent";
import {
  FilterTagsinterface,
  FilterTagsReqRes,
  useLazyGetFilterTagsQuery,
  useUpdateFilterTagsMutation,
} from "../../../redux/services/filterTagsService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import BreadCrumbs from "../../../components/BreadCrumbs";

interface Tag {
  id: number;
  name: string;
  visible: boolean;
}

const FilterTags = () => {
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  const restaurantId: string = selectedRestaurantId;

  const [tagGroups, setTagGroups] = useState<any[]>([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingTag, setEditingTag] = useState({ groupId: null, tagId: null });
  const [newTagName, setNewTagName] = useState("");
  const [addingTagToGroup, setAddingTagToGroup] = useState(null);
  const [newTagInput, setNewTagInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [updateFilterTags] = useUpdateFilterTagsMutation();
  const [
    triggerGetFilterTags,
    {
      data: filterTagsData,
      isLoading: isfilterTagsDataLoading,
      error: isErrorfilterTagsDataLoading,
    },
  ] = useLazyGetFilterTagsQuery();

  useEffect(() => {
    if (restaurantId) {
      triggerGetFilterTags(restaurantId);
    }
  }, [restaurantId, triggerGetFilterTags]);

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: any) => {
    setTagGroups(
      tagGroups.map((group) =>
        group.id === groupId ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  // Toggle tag visibility
  const toggleTagVisibility = (groupId: any, tagId: any) => {
    setTagGroups(
      tagGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              tags: group.tags.map((tag: Tag) =>
                tag.id === tagId ? { ...tag, visible: !tag.visible } : tag
              ),
            }
          : group
      )
    );
  };

  // Start editing group
  const startEditGroup = (groupId: any, currentName: any) => {
    setEditingGroup(groupId);
    setNewGroupName(currentName);
  };

  // Save edited group
  const saveEditedGroup = (groupId: any) => {
    if (newGroupName.trim()) {
      setTagGroups(
        tagGroups.map((group) =>
          group.id === groupId ? { ...group, name: newGroupName.trim() } : group
        )
      );
      setEditingGroup(null);
      setNewGroupName("");
    }
  };

  // Start editing tag
  const startEditTag = (groupId: any, tagId: any, currentName: any) => {
    setEditingTag({ groupId, tagId });
    setNewTagName(currentName);
  };

  // Save edited tag
  const saveEditedTag = (groupId: any, tagId: any) => {
    if (newTagName.trim()) {
      setTagGroups(
        tagGroups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                tags: group.tags.map((tag: Tag) =>
                  tag.id === tagId ? { ...tag, name: newTagName.trim() } : tag
                ),
              }
            : group
        )
      );
      setEditingTag({ groupId: null, tagId: null });
      setNewTagName("");
    }
  };

  // Delete a tag
  const deleteTag = (groupId: any, tagId: any) => {
    setTagGroups(
      tagGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              tags: group.tags.filter((tag: Tag) => tag.id !== tagId),
            }
          : group
      )
    );
  };

  // Delete a group
  const deleteGroup = (groupId: any) => {
    setTagGroups(tagGroups.filter((group) => group.id !== groupId));
  };

  // Start adding new tag to group
  const startAddingTag = (groupId: any) => {
    setAddingTagToGroup(groupId);
    setNewTagInput("");
  };

  // Add new tag to group
  const addNewTag = (groupId: any) => {
    if (newTagInput.trim()) {
      const maxId = Math.max(
        ...tagGroups.flatMap((group) => group.tags).map((tag) => tag.id),
        0
      );

      setTagGroups(
        tagGroups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                tags: [
                  ...group.tags,
                  { id: maxId + 1, name: newTagInput.trim(), visible: true },
                ],
              }
            : group
        )
      );
      setAddingTagToGroup(null);
      setNewTagInput("");
    }
  };

  // Add new group
  const addNewGroup = () => {
    if (newGroupName.trim()) {
      const maxGroupId = Math.max(...tagGroups.map((group) => group.id), 0);

      setTagGroups([
        ...tagGroups,
        {
          id: maxGroupId + 1,
          name: newGroupName.trim(),
          expanded: true,
          tags: [],
          allowMultipleSelect: false,
        },
      ]);
      setNewGroupName("");
    }
  };

  const toggleAllowMultipleSelect = (groupId: any) => {
    setTagGroups(
      tagGroups.map((group) =>
        group.id === groupId
          ? { ...group, allowMultipleSelect: !group.allowMultipleSelect }
          : group
      )
    );
  };

  const handleSaveAllChanges = () => {
    setIsSaving(true);
    const updatedGroups: FilterTagsinterface = {};

    tagGroups.forEach((group) => {
      updatedGroups[group.name] = {
        allowMultipleSelect: group.allowMultipleSelect,
        tags: group.tags.map((tag: Tag) => ({
          tag: tag.name,
          show: tag.visible,
        })),
      };
    });

    const data: FilterTagsReqRes = {
      restaurantId: restaurantId,
      filterTags: updatedGroups,
    };

    updateFilterTags(data)
      .unwrap()
      .then(() => {
        toast.success("Filter tags updated successfully");
        triggerGetFilterTags(restaurantId);
        setIsSaving(false);
      })
      .catch((error) => {
        console.error("Error updating filter tags:", error);
        toast.error("Error updating filter tags");
        setIsSaving(false);
      });
  };

  useEffect(() => {
    if (filterTagsData && filterTagsData.filterTags) {
      const data = filterTagsData.filterTags;

      const transformedGroups = Object.entries(data).map(
        ([groupName, groupValue]: any, groupIndex) => ({
          id: groupIndex + 1,
          name: groupName,
          expanded: true,
          allowMultipleSelect: groupValue.allowMultipleSelect,
          tags: groupValue.tags.map((tagObj: any, tagIndex: number) => ({
            id: (groupIndex + 1) * 100 + tagIndex + 1,
            name: tagObj.tag,
            visible: tagObj.show,
          })),
        })
      );

      setTagGroups(transformedGroups);
    }
  }, [filterTagsData, restaurantId, triggerGetFilterTags]);

  if (isErrorfilterTagsDataLoading) {
    toast.error("Error fetching filter tags data");
  }
  return (
    <><BreadCrumbs hasSubMenus={true}/>
    <div className="py-4">
      <div className="mx-auto bg-white rounded shadow-sm p-4">
        <h5 className="mb-4">Filter Tags Manager</h5>

        {/* Add new group */}
        <Row className="mb-4 d-flex align-items-center justify-content-between">
          <Col lg="10">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="New filter group name"
              className="form-control"
            />
          </Col>
          <Col lg="2" className="d-flex justify-content-end align-items-center">
            <ButtonComponent
              titel="Add Group"
              onClick={addNewGroup}
              icon={<FontAwesomeIcon icon={faCirclePlus} />}
            />
          </Col>
        </Row>

        {/* Group list */}
        {isfilterTagsDataLoading ? (
          <TableLoadingComponent numColumns={3} />
        ) : (
          <div className="mb-3">
            {tagGroups.map((group) => (
              <div key={group.id} className="border rounded mb-3">
                {/* Group header */}
                <div className="bg-light px-3 py-2 d-flex justify-content-between align-items-center">
                  {editingGroup === group.id ? (
                    <div className="d-flex flex-grow-1 align-items-center gap-2">
                      <input
                        type="text"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="form-control"
                        autoFocus
                      />
                      <button
                        onClick={() => saveEditedGroup(group.id)}
                        className="btn btn-outline-success btn-sm"
                      >
                        <FontAwesomeIcon icon={faFloppyDisk} />
                      </button>
                      <button
                        onClick={() => setEditingGroup(null)}
                        className="btn btn-outline-danger btn-sm"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <button
                        onClick={() => toggleGroupExpansion(group.id)}
                        className="btn btn-light btn-sm"
                      >
                        <FontAwesomeIcon
                          icon={group.expanded ? faChevronUp : faChevronDown}
                        />
                      </button>
                      <span className="fw-bold">{group.name}</span>
                    </div>
                  )}

                  {/* {editingGroup !== group.id && (
                                        <div className="d-flex gap-1">
                                            <EditButtonComponent onClick={() => startEditGroup(group.id, group.name)} />
                                            <DeleteButtonComponent onClick={() => deleteGroup(group.id)} />
                                        </div>
                                    )} */}
                  {editingGroup !== group.id && (
                    <div className="d-flex gap-1 align-items-center">
                      <div className="form-check form-switch me-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`multi-select-${group.id}`}
                          checked={group.allowMultipleSelect}
                          onChange={() => toggleAllowMultipleSelect(group.id)}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor={`multi-select-${group.id}`}
                          title="Allow multiple selection"
                        >
                          Allow Multi
                        </label>
                      </div>
                      <EditButtonComponent
                        onClick={() => startEditGroup(group.id, group.name)}
                      />
                      <DeleteButtonComponent
                        onClick={() => deleteGroup(group.id)}
                      />
                    </div>
                  )}
                </div>

                {/* Group content */}
                {group.expanded && (
                  <div className="p-3">
                    {/* Tags list */}
                    {group.tags.map((tag: Tag) => (
                      <div
                        key={tag.id}
                        className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                      >
                        {editingTag.groupId === group.id &&
                        editingTag.tagId === tag.id ? (
                          <div className="d-flex flex-grow-1 gap-2">
                            <input
                              type="text"
                              value={newTagName}
                              onChange={(e) => setNewTagName(e.target.value)}
                              className="form-control"
                              autoFocus
                            />
                            <button
                              onClick={() => saveEditedTag(group.id, tag.id)}
                              className="btn btn-outline-success btn-sm"
                            >
                              <FontAwesomeIcon icon={faFloppyDisk} />
                            </button>
                            <button
                              onClick={() =>
                                setEditingTag({ groupId: null, tagId: null })
                              }
                              className="btn btn-outline-danger btn-sm"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span>{tag.name}</span>
                            <div className="d-flex gap-1">
                              <ViewButtonComponentV2
                                visible={tag.visible}
                                onClick={() =>
                                  toggleTagVisibility(group.id, tag.id)
                                }
                              />
                              <EditButtonComponent
                                onClick={() =>
                                  startEditTag(group.id, tag.id, tag.name)
                                }
                              />
                              <DeleteButtonComponent
                                onClick={() => deleteTag(group.id, tag.id)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    ))}

                    {/* Add new tag */}
                    {addingTagToGroup === group.id ? (
                      <Row className="mt-3 d-flex align-items-center justify-content-between">
                        <Col lg="10">
                          <input
                            type="text"
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            className="form-control"
                            placeholder="Tag name"
                            autoFocus
                          />
                        </Col>
                        <Col
                          lg="2"
                          className="d-flex justify-content-end align-items-center"
                        >
                          <div className="d-flex gap-1">
                            <button
                              onClick={() => addNewTag(group.id)}
                              className="btn btn-success btn-sm"
                            >
                              <FontAwesomeIcon
                                icon={faFloppyDisk}
                                className="me-2"
                              />
                              <span>Save</span>
                            </button>
                            <button
                              onClick={() => setAddingTagToGroup(null)}
                              className="btn btn-secondary btn-sm"
                            >
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="me-2"
                              />
                              <span>Cancel</span>
                            </button>
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <ButtonComponent
                        titel="Add Tag"
                        onClick={() => startAddingTag(group.id)}
                        icon={<FontAwesomeIcon icon={faCirclePlus} />}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="d-flex justify-content-end">
          {isSaving ? (
            <ButtonComponent
              titel="Saving..."
              isDisabled={true}
              icon={<FontAwesomeIcon icon={faSpinner} spin />}
            />
          ) : (
            <ButtonComponent
              titel="Save All Changes"
              onClick={handleSaveAllChanges}
              icon={<FontAwesomeIcon icon={faFloppyDisk} />}
            />
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default FilterTags;
